import { PARTNER_PLAN_KEYS } from './partnerAcceptPlans';

export const normalizePartnerSubscriptionPlan = (plan = {}) => {
  const key = String(plan.key ?? '')
    .trim()
    .toLowerCase();
  const amount = Number(plan.amount);

  return {
    pricingId: plan.pricingId ?? plan.id ?? null,
    planId: plan.planId ?? null,
    key,
    name: String(plan.name ?? key).trim() || key,
    sortOrder: Number.isFinite(Number(plan.sortOrder)) ? Number(plan.sortOrder) : 0,
    amount: Number.isFinite(amount) ? amount : 0,
    currency:
      String(plan.currency ?? 'AED')
        .trim()
        .toUpperCase() || 'AED',
  };
};

export const normalizePartnerPlansResponse = (payload = {}) => {
  const root = payload?.data ?? payload ?? {};
  const plans = (root.subscriptionPlans ?? root.plans ?? [])
    .filter(Boolean)
    .map(normalizePartnerSubscriptionPlan)
    .filter((plan) => plan.key)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.amount - b.amount);

  const currentPlanRaw = root.currentPlan ?? null;
  const currentPlan =
    currentPlanRaw && typeof currentPlanRaw === 'object'
      ? normalizePartnerSubscriptionPlan(currentPlanRaw)
      : currentPlanRaw
        ? normalizePartnerSubscriptionPlan({ key: currentPlanRaw })
        : null;

  return {
    subscriptionPlans: plans,
    currentPlan,
    currentPlanKey: currentPlan?.key ?? null,
  };
};

export const pickPartnerCheckoutUrl = (payload = {}) => {
  const root = payload?.data ?? payload ?? {};
  const candidates = [
    root.checkoutUrl,
    root.redirectUrl,
    root.paymentUrl,
    root.url,
    root.link,
    root.sessionUrl,
    root?.checkout?.url,
    root?.session?.url,
  ];

  for (const value of candidates) {
    const url = String(value ?? '').trim();
    if (url) return url;
  }

  return null;
};

/** Marketing copy + card chrome keyed by plan `key` from GET /admin/plans. */
export const PARTNER_SUBSCRIPTION_PLAN_PRESENTATION = {
  [PARTNER_PLAN_KEYS.BASIC]: {
    description: 'Run clubs, pickup games, and fitness meetups filling recurring sessions.',
    headerColor: '#7B8FA1',
    amountColor: '#1C1917',
    badge: null,
    features: ['2 events/month', 'Core dashboard', 'RSVP + attendance tracking'],
  },
  [PARTNER_PLAN_KEYS.PREMIUM]: {
    description:
      'Interest groups, wellness circles, and social clubs bringing members together regularly.',
    headerColor: '#E07A7A',
    amountColor: '#E07A7A',
    badge: 'MOST COMMON',
    features: ['4 events/month', 'Dashboard + attendee analytics', 'Dedicated group chat'],
  },
  [PARTNER_PLAN_KEYS.PRO]: {
    description: 'Corporate mixers, launches, and client-facing events for brands and companies.',
    headerColor: '#1C1917',
    amountColor: '#1C1917',
    badge: null,
    features: [
      'Unlimited events',
      'Priority placement + full analytics',
      'Dedicated account support',
    ],
  },
};

const DEFAULT_PRESENTATION = {
  description: 'Choose the plan that fits how you bring people together.',
  headerColor: '#7B8FA1',
  amountColor: '#1C1917',
  badge: null,
  features: [],
};

export const getPartnerSubscriptionPlanPresentation = (planKey) => {
  const key = String(planKey ?? '')
    .trim()
    .toLowerCase();
  return PARTNER_SUBSCRIPTION_PLAN_PRESENTATION[key] ?? DEFAULT_PRESENTATION;
};

export const formatPartnerPlanAmount = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

export const PARTNER_SUBSCRIPTION_PATH = '/partner-subscription';

export const buildPartnerPlanCheckoutReturnUrls = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const base = `${origin}${PARTNER_SUBSCRIPTION_PATH}`;
  return {
    successUrl: `${base}?checkout=success`,
    cancelUrl: `${base}?checkout=cancel`,
    failureUrl: `${base}?checkout=failure`,
  };
};
